import { canadianProvincesTerritories } from 'common/src/constants/canadianProvincesTerritories';
import { countries, Country, usaName } from 'common/src/constants/countries';
import { usStates } from 'common/src/constants/usStates';
import { VALIDATION_REGEX } from 'common/src/validators/validationRegex';

import { asDate, validateEndDate } from 'portal/src/validators/dateValidator';

import {
  autoCompleteType,
  datePickerType,
  patternInputType,
  textInputType,
} from './FormFieldTypes';
import { VALIDATION_MSG } from './validationMessages';

const getStateProvinceLabel = (country: Country) =>
  country === usaName ? 'State' : 'Province/Territory';

const getStateOptions = (country: Country) =>
  (country === usaName ? usStates : canadianProvincesTerritories).map(
    (state) => ({
      label: `${state.name} (${state.abbreviation})`,
      value: state.abbreviation,
      id: state.abbreviation,
    }),
  );

export const getStateProvinceField = (
  country: Country | null,
  required: boolean,
  addressReadOnly: boolean,
  alernateFieldName = 'state',
): autoCompleteType => {
  const stateProvinceLabel = getStateProvinceLabel(country ?? usaName);
  const stateOptions = getStateOptions(country ?? usaName);

  return {
    placeholder: stateProvinceLabel,
    label: stateProvinceLabel,
    name: alernateFieldName,
    type: 'autocomplete',
    required,
    options: stateOptions,
    disabled: addressReadOnly,
    disableClearable: required,
  };
};

export const getZipPostalCodeField = (
  country: Country | null,
  required: boolean,
  addressReadOnly: boolean,
  alternateFieldName = 'postalCode',
): textInputType | patternInputType =>
  country === usaName || !country
    ? {
        placeholder: 'Zip Code',
        label: 'Zip Code',
        name: alternateFieldName,
        type: 'pattern',
        required,
        disabled: addressReadOnly,
        maskPattern: '00000 0000',
        validate: (value: string) =>
          VALIDATION_REGEX.usaZipCode.test(value) ||
          VALIDATION_MSG.invalidZipCode,
      }
    : {
        placeholder: 'Postal Code',
        label: 'Postal Code',
        name: alternateFieldName,
        type: 'pattern',
        definitions: {
          A: /[ABCEGHJKLMNPRSTVXY]/i,
        },
        required,
        disabled: addressReadOnly,
        maskPattern: 'A0A 0A0',
        validate: (value: string) =>
          VALIDATION_REGEX.canadianPostalCode.test(value) ||
          VALIDATION_MSG.invalidPostalCode,
      };

export const getAddressFields = (
  required: boolean,
  addressReadOnly: boolean,
  country?: Country,
): (autoCompleteType | textInputType | patternInputType)[] => {
  let defaultCountry: Country = usaName;

  if (country) {
    defaultCountry = country;
  }

  const stateProvinceField = getStateProvinceField(
    defaultCountry,
    required,
    addressReadOnly,
  );
  const zipPostalCodeField = getZipPostalCodeField(
    defaultCountry,
    required,
    addressReadOnly,
  );
  return [
    {
      placeholder: 'Address 1',
      label: 'Address 1',
      name: 'address1',
      type: 'textInput',
      required,
      disabled: addressReadOnly,
    },
    {
      placeholder: 'Address 2',
      label: 'Address 2',
      name: 'address2',
      type: 'textInput',
      required: false,
      disabled: addressReadOnly,
    },
    {
      placeholder: 'City',
      label: 'City',
      name: 'city',
      type: 'textInput',
      required,
      disabled: addressReadOnly,
    },
    stateProvinceField,
    zipPostalCodeField,
    {
      placeholder: 'Country',
      label: 'Country',
      name: 'country',
      type: 'autocomplete',
      required,
      options: countries.map((item) => ({
        label: `${item.name} (${item.abbreviation})`,
        value: item.name,
      })),
      disabled: addressReadOnly,
      disableClearable: required,
    },
  ];
};

export const getPhoneNumberField = (
  isRequired: boolean,
  alternateFieldName = 'phone',
): patternInputType => ({
  placeholder: 'Phone',
  label: 'Phone',
  name: alternateFieldName,
  type: 'pattern',
  required: isRequired,
  maskPattern: '(000) 000-0000',
  unmask: true,
  validate: (value: string) =>
    VALIDATION_REGEX.usaPhone.test(value.trim()) ||
    VALIDATION_MSG.invalidPhoneNumber,
});

export const getEmailField = (
  isRequired: boolean,
  disabled = false,
  alternateFieldName = 'email',
): textInputType => ({
  placeholder: 'Email',
  label: 'Email',
  name: alternateFieldName,
  type: 'textInput',
  required: isRequired,
  disabled,
  validate: (value: string) =>
    VALIDATION_REGEX.email.test(value.trim()) || VALIDATION_MSG.invalidEmail,
});

export const getStartAndEndDateFields = ({
  startFieldName,
  startFieldDisabled = false,
  endFieldName,
  endFieldDisabled = false,
  startLabel,
  endLabel,
  required,
  minDate,
  maxDate,
  onStartChange,
  onEndChange,
}: {
  startFieldName?: string;
  startFieldDisabled?: boolean;
  endFieldName?: string;
  endFieldDisabled?: boolean;
  startLabel?: string;
  endLabel?: string;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  onStartChange?: (value: Date) => void;
  onEndChange?: (value: Date) => void;
}): datePickerType[] => {
  const startLabelText = startLabel || 'Start Date';
  const endLabelText = endLabel || 'End Date';
  const startName = startFieldName || 'startDate';
  const endName = endFieldName || 'endDate';

  return [
    {
      disabled: startFieldDisabled,
      placeholder: startLabelText,
      label: startLabelText,
      name: startName,
      type: 'datePicker',
      minDate,
      maxDate,
      onChange: onStartChange,
      required,
    },
    {
      disabled: endFieldDisabled,
      placeholder: endLabelText,
      label: endLabelText,
      name: endName,
      type: 'datePicker',
      maxDate,
      onChange: onEndChange,
      required,
      validate: (endDate, values) => {
        const parsedEndDate = asDate(endDate);
        const parsedStartDate = asDate(values[startName]);

        if (parsedStartDate && parsedEndDate) {
          return (
            validateEndDate(parsedEndDate, parsedStartDate) ||
            VALIDATION_MSG.invalidEndDate
          );
        }
      },
    },
  ];
};
